/**
 * The house's website, Instagram and Facebook (S50): fields of the business. Typed in or read from an
 * old card in any form („@winzerhof_stuben“, „facebook.com/winzerhof“, „www.winzerhof.de“), kept as
 * one https address each. Phone and e-mail beside them (Martin, 29.10.2026: the Winzerhof's cards name
 * only those), kept as written. On the net real links at the foot of the card (tel:, mailto:); in
 * print, when the house wants it, one line (`linksLine`).
 */
import type { ReadingHouse } from "./menu-reading.ts";

export const HOUSE_LINKS = ["website", "instagram", "facebook"] as const;
export type HouseLink = (typeof HOUSE_LINKS)[number];

const asUrl = (raw: string): URL | null => {
  const s = raw.trim();
  if (!s || /\s|["'<>]/.test(s) || s.length > 300) return null;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(s) ? s : `https://${s}`);
    return url.protocol === "https:" || url.protocol === "http:" ? url : null;
  } catch {
    return null;
  }
};
const hostOf = (url: URL) => url.hostname.toLowerCase().replace(/^(www|m|de-de)\./, "");

/** The handle of an Instagram account, from „@name“, „name“ or its address; null: none. */
function instagramHandle(raw: string): string | null {
  const s = raw.trim().replace(/^@/, "");
  if (/^[A-Za-z0-9._]{1,30}$/.test(s) && !/^\.+$/.test(s)) return s;
  const url = asUrl(raw);
  if (!url || hostOf(url) !== "instagram.com") return null;
  const name = url.pathname.split("/").filter(Boolean)[0] ?? "";
  return /^[A-Za-z0-9._]{1,30}$/.test(name) ? name : null;
}

/** The page of a Facebook account, from „name“, „/name“ or its address (also profile.php?id=…); null: none. */
function facebookPath(raw: string): string | null {
  const s = raw.trim().replace(/^@|^\//, "");
  if (/^[A-Za-z0-9.-]{1,80}$/.test(s) && !/^[.-]+$/.test(s) && !/\.(de|com|net|org|eu|info)$/i.test(s)) return s;
  const url = asUrl(raw);
  if (!url || !["facebook.com", "fb.com"].includes(hostOf(url))) return null;
  const id = url.searchParams.get("id");
  if (url.pathname === "/profile.php" && id && /^\d{1,20}$/.test(id)) return `profile.php?id=${id}`;
  const parts = url.pathname.split("/").filter(Boolean);
  const name = parts[0] === "pages" ? parts.slice(1).join("/") : (parts[0] ?? "");
  return /^[A-Za-z0-9./-]{1,120}$/.test(name) && !/\.\./.test(name) ? name : null;
}

/** One field as the business keeps it: an https address, or "" when it is empty or no such address. */
export function houseLink(kind: HouseLink, raw: unknown): string {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return "";
  if (kind === "instagram") {
    const h = instagramHandle(s);
    return h ? `https://www.instagram.com/${h}/` : "";
  }
  if (kind === "facebook") {
    const p = facebookPath(s);
    return p ? `https://www.facebook.com/${p}` : "";
  }
  const url = asUrl(s);
  if (!url || !url.hostname.includes(".") || ["instagram.com", "facebook.com", "fb.com"].includes(hostOf(url))) return "";
  url.protocol = "https:";
  return url.href.length <= 300 ? url.href : "";
}

/** A phone number as the house writes it („06222 9520“, „+49 6222 9520“), or "" when it is none. */
export function housePhone(raw: unknown): string {
  const s = typeof raw === "string" ? raw.replace(/\s+/g, " ").trim() : "";
  const digits = s.replace(/\D/g, "");
  return /^\+?[\d ()/.-]{4,30}$/.test(s) && digits.length >= 4 && digits.length <= 16 ? s : "";
}

/** The phone number to call: „tel:062229520“, „tel:+4962229520“. */
export const telLink = (phone: string) => `tel:${phone.trim().startsWith("+") ? "+" : ""}${phone.replace(/\D/g, "")}`;

/** An e-mail address, or "" when it is none. */
export function houseEmail(raw: unknown): string {
  const s = typeof raw === "string" ? raw.trim().replace(/^mailto:/i, "") : "";
  return /^[^\s@"'<>(),;:]{1,64}@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i.test(s) && s.length <= 120 ? s : "";
}

/** How a link reads on paper: „winzerhof-stuben.de“, „Instagram @winzerhof_stuben“, „facebook.com/winzerhof“. */
export function houseLinkLabel(kind: HouseLink, link: string): string {
  const url = asUrl(link);
  if (!url) return "";
  if (kind === "instagram") return `Instagram @${url.pathname.split("/").filter(Boolean)[0] ?? ""}`;
  if (kind === "facebook") return `facebook.com/${`${url.pathname}${url.search}`.replace(/^\//, "")}`;
  return `${hostOf(url)}${url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "")}${url.search}`;
}

/** The line in print: phone, e-mail and the links the business has, one after the other; "" when it has none. */
export function linksLine(links: Partial<Record<HouseLink | "phone" | "email", string>>): string {
  const contact = [links.phone ? `Tel. ${links.phone}` : "", links.email ?? ""];
  return [...contact, ...HOUSE_LINKS.map((k) => (links[k] ? houseLinkLabel(k, links[k]!) : ""))].filter(Boolean).join(" · ");
}

/** What a reading suggests for the settings (S50): each field the card names and the business has otherwise. */
export function houseSuggestions(house: ReadingHouse, current: { address: string } & Partial<Record<HouseLink | "phone" | "email", string>>): { field: keyof ReadingHouse; value: string }[] {
  const out: { field: keyof ReadingHouse; value: string }[] = [];
  const address = house.address.replace(/\s+/g, " ").trim().slice(0, 300);
  if (address && address !== current.address) out.push({ field: "address", value: address });
  const phone = housePhone(house.phone);
  if (phone && phone !== (current.phone ?? "")) out.push({ field: "phone", value: phone });
  const email = houseEmail(house.email);
  if (email && email.toLowerCase() !== (current.email ?? "").toLowerCase()) out.push({ field: "email", value: email });
  for (const k of HOUSE_LINKS) {
    const value = houseLink(k, house[k]);
    if (value && value !== (current[k] ?? "")) out.push({ field: k, value });
  }
  return out;
}
